// src/features/readingnote/pages/ReadingNotePage.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePageScale } from "@/ui/usePageScale";
import { PageShell } from "@/ui/PageShell";
import { HEADER, withBase } from "@/ui/Header";
import { me, reviewsMy, reviewDelete, calendarList } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { registerPager, unregisterPager } from "@/lib/pagerBridge";
import { ActionSheet } from "@/ui/ActionSheet";
import { ReadingCalendar, type CalendarEntry } from "@/features/readingnote/components/ReadingCalendar";
import { customConfirm } from "@/ui/confirm";
import { toCoverUrl } from "@/lib/coverUrl";
import { SPC_MODEL, getDeviceModel } from "@/lib/androidBridge";
import { ReviewCardModal } from "@/features/readingnote/components/ReviewCardModal";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";


/* ───────────────── Config ───────────────── */
const BASE = {
    W: 1264,
    MARGIN_X: 137,
    TAB_H: 78,
    TAB_FZ: 35,
    GAP: 24,

    THUMB_W: 230,
    THUMB_H: 310,
    TITLE_FZ: 34,
    BODY_FZ: 28,
    ROW_VPAD: 18,
    ROW_GAP: 20,

    PAGER_H: 61,
    PAGER_TOP_GAP: 0,
} as const;

const ICONS = {
    header: withBase("icons/reading_note.svg"),
    first:  withBase("icons/prev2.svg"),
    prev:   withBase("icons/prev1.svg"),
    next:   withBase("icons/next1.svg"),
    last:   withBase("icons/next2.svg"),
};

function StarIcon({ filled, size }: { filled: boolean; size: number }) {
    const fill = filled ? "#111" : "#D1D5DB";
    const stroke = filled ? "#111" : "#D1D5DB";

    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
            <path
                d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                fill={fill}
                stroke={stroke}
                strokeWidth={2.6}
                strokeLinejoin="round"
            />
        </svg>
    );
}


/* ──────────────── Scale ─────────────── */
function useScale() {
    const { s, px, sf, sfpx } = usePageScale({ baseW: BASE.W, max: 1 });
    return { s, px, sf, sfpx };
}

/* ──────────────── Full-bleed Tabs ─────────────── */
type TabKey = "REVIEWS" | "CALENDAR";
function TabsFull({
                      value, onChange, px, s, sfpx
                  }:{ value:TabKey; onChange:(v:TabKey)=>void; px:(n:number)=>string; s:(n:number)=>number; sfpx:(n:number)=>string }) {
    const Tab = ({ keyVal, label }:{ keyVal:TabKey; label:string }) => {
        const active = value===keyVal;
        return (
            <button
                onClick={()=>onChange(keyVal)}
                style={{
                    height:px(BASE.TAB_H),
                    width:"100%",
                    fontSize:sfpx(BASE.TAB_FZ),
                    fontWeight:FW.extraBold,
                    background:"transparent",
                    border:"none",
                    cursor:"pointer",
                    color:COLOR.text,
                    borderBottom: active ? `${s(4)}px solid #000` : `${s(2)}px solid #e5e7eb`,
                }}
            >
                {label}
            </button>
        );
    };
    return (
        <div style={{
            width:"100%",
            display:"grid",
            gridTemplateColumns:"1fr 1fr",
            margin:"0 auto"
        }}>
            <Tab keyVal="REVIEWS"  label="나의 리뷰" />
            <Tab keyVal="CALENDAR" label="독서 달력" />
        </div>
    );
}

/* ──────────────── Pager (라이브러리 룩) ─────────────── */
function Pager({
                   page, totalPages, hasNext, onFirst, onPrev, onNext, onLast, px, sfpx
               }:{
    page:number; totalPages:number; hasNext:boolean;
    onFirst:()=>void; onPrev:()=>void; onNext:()=>void; onLast:()=>void; px:(n:number)=>string; sfpx:(n:number)=>string;
}) {
    return (
        <div style={{ width:"100%", height:"100%" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%" }}>
                <button disabled={!(page>1)} onClick={onFirst}
                        style={{ lineHeight:0, background:"transparent", marginRight:px(48), border:"none", opacity:page>1?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.first} alt="" style={{ width:px(48), height:px(48), display:"block" }} />
                </button>
                <button disabled={!(page>1)} onClick={onPrev}
                        style={{ lineHeight:0, background:"transparent", marginRight:px(46), border:"none", opacity:page>1?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.prev} alt="" style={{ width:px(48), height:px(48), display:"block" }} />
                </button>
                <span style={{ fontWeight:700, fontFeatureSettings:"'tnum' 1", fontSize:sfpx(34), minWidth:px(120), textAlign:"center", marginRight:px(46) }}>
          {Math.max(1,page)} / {Math.max(1,totalPages)}
        </span>
                <button disabled={!hasNext} onClick={onNext}
                        style={{ lineHeight:0, background:"transparent", marginRight:px(48), border:"none", opacity:hasNext?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.next} alt="" style={{ width:px(48), height:px(48), display:"block" }} />
                </button>
                <button disabled={!hasNext} onClick={onLast}
                        style={{ lineHeight:0, background:"transparent", border:"none", opacity:hasNext?1:0.4, cursor:"pointer" }}>
                    <img src={ICONS.last} alt="" style={{ width:px(48), height:px(48), display:"block" }} />
                </button>
            </div>
        </div>
    );
}

/* ──────────────── Long-press helper ─────────────── */
function useLongPress(onLong:()=>void, ms=500) {
    const t = useRef<number|null>(null);
    const fired = useRef(false);
    const start = () => { fired.current=false; t.current = window.setTimeout(()=>{ fired.current=true; onLong(); }, ms) as any; };
    const stop  = () => { if(t.current) clearTimeout(t.current); t.current=null; };
    return { bind:{ onPointerDown:start, onPointerUp:stop, onPointerLeave:stop, onTouchStart:start, onTouchEnd:stop, onMouseDown:start, onMouseUp:stop }, fired };
}

/* ──────────────── Row (크게, 1열) ─────────────── */
function ReviewRow({
                       it, px, s, sfpx, thumbW, thumbH, onOpenBook, onOpenSheet
                   }:{ it:any; px:(n:number)=>string; s:(n:number)=>number; sfpx:(n:number)=>string; thumbW:number; thumbH:number; onOpenBook:(it:any)=>void; onOpenSheet:(id:number)=>void }) {
    const { bind, fired } = useLongPress(()=>onOpenSheet(it.id), 500);
    const keywords: string[] = it.keywords || [];

    return (
        <div
            {...bind}
            onClick={()=>{ if (fired.current) { fired.current=false; return; } onOpenBook(it); }}
            style={{
                display:"grid",
                gridTemplateColumns:`${thumbW}px 1fr auto`,
                gap:px(BASE.ROW_GAP),
                padding:`${px(BASE.ROW_VPAD)} 0`,
                borderBottom:BORDER.muted,
                cursor:"pointer",
                userSelect:"none",
                height:`${thumbH + s(BASE.ROW_VPAD) * 2}px`,
                overflow:"hidden",
                boxSizing:"border-box",
            }}
        >
            {/* 썸네일: contain + 레터박스 */}
            <div
                style={{
                    width:`${thumbW}px`,
                    height:`${thumbH}px`,
                    display:"grid",
                    placeItems:"center",
                    background:COLOR.bgLighter,
                    border:BORDER.light,
                    borderRadius:RADIUS.md,
                    overflow:"hidden"
                }}
            >
                {it.Book?.cover_url ? (
                    <img
                        src={toCoverUrl(it.Book.cover_url)}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        style={{
                            maxWidth:"100%", maxHeight:"100%",
                            objectFit:"contain", display:"block"
                        }}
                    />
                ) : (
                    <span style={{ color:"#9ca3af", fontSize:12 }}>No Image</span>
                )}
            </div>

            {/* 텍스트 영역 */}
            <div style={{ display:"flex", flexDirection:"column", minWidth:0, height:"100%", overflow:"hidden" }}>
                <div style={{ flexShrink:0, fontWeight:FW.extraBold, marginBottom:8, fontSize:sfpx(BASE.TITLE_FZ), lineHeight:1.22, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {it.Book?.title || "제목 없음"}
                </div>
                {/* 본문: 남은 공간 채우고, 공간 부족 시 위에서 잘림 */}
                <div style={{ flex:1, minHeight:0, overflow:"hidden" }}>
                    <div
                        style={{
                            color:"#444", fontSize:sfpx(BASE.BODY_FZ), lineHeight:1.55,
                            display:"-webkit-box", WebkitBoxOrient:"vertical" as any, WebkitLineClamp:3 as any, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {String(it.content || "")}
                    </div>
                </div>
                {/* 별(본문 아래) -> 키워드(그 아래): 항상 보임 */}
                <div style={{ flexShrink:0, display: "flex", flexDirection: "column", gap: px(7), marginTop: px(8) }}>
                    {/* 1) 별 줄 */}
                    <div style={{ display: "flex", alignItems: "center", gap: px(4) }}>
                        {(() => {
                            const r = Math.max(0, Math.min(5, Number(it.rating || 0)));
                            const starSize = Math.round(s(24));
                            return (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: px(4) }}>
                      {[1, 2, 3, 4, 5].map((n) => (
                          <StarIcon key={n} filled={n <= r} size={starSize} />
                      ))}
                    </span>
                            );
                        })()}
                    </div>

                    {/* 2) 키워드 줄 */}
                    {keywords.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: px(6), alignItems: "center" }}>
                            {keywords.map((k, i) => (
                                <span
                                    key={i}
                                    style={{
                                        display: "inline-block",
                                        padding: `${px(4)} ${px(10)}`,
                                        fontSize: sfpx(19),
                                        fontWeight: 700,
                                        borderRadius: RADIUS.full,
                                        border: BORDER.dark,
                                        background: COLOR.white,
                                        color: COLOR.black,
                                        whiteSpace: "nowrap",
                                        lineHeight: 1.3,
                                    }}
                                >{k}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

            </div>

            {/* 오른쪽: ⋯ 버튼 + 좋아요 */}
            <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", justifyContent:"space-between", height:"100%" }}>
                <button
                    onClick={(e)=>{ e.stopPropagation(); onOpenSheet(it.id); }}
                    style={{ background:"transparent", border:"1px solid #ddd", borderRadius:10, padding:`${px(8)} ${px(12)}` }}
                >
                    ⋯
                </button>
                <span style={{ color:"#999", fontSize:sfpx(16), whiteSpace:"nowrap" }}>좋아요 {it.likes_count || 0}</span>
            </div>
        </div>
    );
}

/* ──────────────── Page ─────────────── */
export default function ReadingNotePage(){
    const { s, px, sfpx } = useScale();
    const nav = useNavigate();

    const [tab, setTab] = useState<TabKey>("REVIEWS");

    // 페이지당 3개(3행 고정)
    const pageSize = 3;
    const [items,setItems] = useState<any[]>([]);
    const [total,setTotal] = useState(0);
    const [page,setPage] = useState(1);

    const [sheet,setSheet] = useState<{open:boolean; id?:number; item?:any}>({ open:false });

    // 카드 저장 모달
    const [cardReview, setCardReview] = useState<any | null>(null);
    const [cardModalOpen, setCardModalOpen] = useState(false);
    const userNickname = (() => {
        try {
            const u = JSON.parse(localStorage.getItem("bt.user") || "{}");
            return u.nickname || u.name || "";
        } catch { return ""; }
    })();

    const actions = React.useMemo(() => {
        if (!sheet.open || !sheet.id) return [];
        return [
            { label: "수정", onClick: () => nav(`/reviews/${sheet.id}/edit`, { state: { backTo: "/readingnote" } }) },
            {
                label: "카드 저장하기",
                onClick: () => {
                    const rev = sheet.item;
                    if (rev) {
                        setCardReview({
                            ...rev,
                            created_at: rev.created_at || rev.createdAt || "",
                        });
                        setCardModalOpen(true);
                    }
                },
            },
            { label: "삭제", danger: true, onClick: async () => { await del(sheet.id!); } },
        ];
    }, [sheet, nav]);

    const [entries, setEntries] = useState<CalendarEntry[]>([]);
    const loadMonth = useCallback(async (d: Date) => {
        try {
            const ym = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
            const r = await calendarList(ym);
            setEntries(r.items || []);
        } catch (e) {
            console.warn('[calendarList] fail', e);
            setEntries([]);
        }
    }, []);

    useEffect(()=> {
      if (tab !== "CALENDAR") return;
      loadMonth(new Date());
    }, [tab, loadMonth]);

    useEffect(()=>{ (async()=>{
        try{ await me(); }catch{ nav(`/auth/login?next=${encodeURIComponent(location.pathname)}`); return; }
        if (tab!=="REVIEWS") return;
        const r = await reviewsMy({ page, size: pageSize });
        setItems(r.items||[]); setTotal(r.total||0);
    })(); },[page, tab, nav]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const hasNext = page < totalPages;

    // 물리키 페이저 브릿지 등록
    useEffect(() => {
        registerPager({
            next: () => setPage(p => Math.min(totalPages, p + 1)),
            prev: () => setPage(p => Math.max(1, p - 1)),
            first: () => setPage(1),
            last: () => setPage(totalPages),
        });
        return () => unregisterPager();
    }, [totalPages]);

    const refresh = async ()=> {
        const r = await reviewsMy({ page, size: pageSize });
        setItems(r.items||[]); setTotal(r.total||0);
    };

    const del = async(id:number)=>{
        const ok = await customConfirm("리뷰를 삭제할까요?");
        if(!ok) return;
        await reviewDelete(id);
        await refresh();
    };

    const openBook = useCallback((it:any)=>{
        const b = it.Book || {};
        const id = b.isbn13 || b.isbn10 || b.id || `work-${encodeURIComponent((b.title||"").slice(0,80))}`;

        nav(`/books/${id}`, {
            state: {
                overrideSynopsis: String(it.content || ""),
                hasMyReview: true,
                myReviewId: it.id,
                myKeywords: it.keywords || [],
                myRating: it.rating || 0,
                myCreatedAt: it.created_at || "",   // 카드에 사용할 작성일
                from: { page, source:"reading-note" }
            }
        });
    }, [nav, page]);

    // SPC: match BookSearchPage card dimensions
    const isSpc = getDeviceModel() === SPC_MODEL;
    const { thumbW, thumbH, pagerH } = React.useMemo(() => {
        if (isSpc) {
            const cg = s(24);
            const cardW = Math.floor((s(1048) - cg * 2) / 3) - 5;
            const ratio = cardW / s(205);
            return { thumbW: cardW, thumbH: Math.round(s(295) * ratio), pagerH: s(140) };
        }
        return { thumbW: s(BASE.THUMB_W), thumbH: s(BASE.THUMB_H), pagerH: s(BASE.PAGER_H) };
    }, [s, isSpc]);

    const rowBlock = thumbH + s(BASE.ROW_VPAD)*2;
    const listH = 3*rowBlock + s(BASE.ROW_GAP)*2;

    return (
        <PageShell title="독서기록" icon={ICONS.header} headerHeight={HEADER.H} width={px(BASE.W)}>
            <div style={{ gridArea:"body", display:"flex", flexDirection:"column", height:"100%" }}>
                {/* ① 탭 */}
                <TabsFull value={tab} onChange={setTab} px={px} s={s} sfpx={sfpx} />

                {/* ② 콘텐츠 — takes remaining space */}
                <div style={{ flex:1, minHeight:0, overflow: (isSpc && tab === "CALENDAR") ? "auto" : "hidden" }}>
                    <div style={{ width: px(BASE.W), margin:"0 auto", height: (isSpc && tab === "CALENDAR") ? "auto" : "100%" }}>
                        <div style={{ marginLeft:px(BASE.MARGIN_X), marginRight:px(BASE.MARGIN_X), paddingTop:px(BASE.GAP) }}>
                            {tab === "REVIEWS" ? (
                                <div style={{ height:`${listH}px`, overflow:"hidden", boxSizing:"border-box" }}>
                                    {total===0 && <div style={{ padding: px(24), color: "#666", fontSize: sfpx(BASE.BODY_FZ) }}>작성한 리뷰가 없습니다.</div>}
                                    {items.map((it:any)=>(
                                        <ReviewRow key={it.id} it={it} px={px} s={s} sfpx={sfpx} thumbW={thumbW} thumbH={thumbH} onOpenBook={openBook} onOpenSheet={(id)=>setSheet({open:true,id,item:it})} />
                                    ))}
                                    {items.length < 3 && Array.from({length: 3 - items.length}).map((_,i)=>(
                                        <div key={`empty-${i}`} style={{ height:`${rowBlock}px`, borderBottom: i<3-items.length-1 ? BORDER.muted : "none" }} />
                                    ))}
                                </div>
                            ) : (
                                <ReadingCalendar
                                    entries={entries}
                                    px={px}
                                    s={s}
                                    sfpx={sfpx}
                                    isSpc={isSpc}
                                    weekStartsOn={0}
                                    onMonthChange={loadMonth}
                                    onDayClick={(_iso)=>{}}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* ③ 페이저 — pinned to bottom, hidden on calendar tab */}
                {tab === "REVIEWS" && (
                    <div style={{ flexShrink:0, height:`${pagerH}px`, marginLeft:px(BASE.MARGIN_X), marginRight:px(BASE.MARGIN_X) }}>
                        <Pager
                            page={page}
                            totalPages={totalPages}
                            hasNext={hasNext}
                            onFirst={()=>setPage(1)}
                            onPrev={()=>setPage(p=>Math.max(1,p-1))}
                            onNext={()=>setPage(p=>Math.min(totalPages,p+1))}
                            onLast={()=>setPage(totalPages)}
                            px={px}
                            sfpx={sfpx}
                        />
                    </div>
                )}
            </div>

            <ActionSheet open={sheet.open} onClose={()=>setSheet({open:false})} actions={actions} />

            {/* 카드 저장 모달 (portal로 document.body에 마운트) */}
            <ReviewCardModal
                open={cardModalOpen && !!cardReview}
                onClose={() => setCardModalOpen(false)}
                review={cardReview ?? { id: 0, content: "", rating: 0, keywords: [], created_at: "" }}
                nickname={userNickname}
            />
        </PageShell>
    );
}
